import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import { ArrowLeft, FilePdf, PaperPlaneTilt, Trash, Warning, CheckCircle, LinkSimple, ArrowRight, PencilSimple, UsersThree, Lightning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import NewProposalModal from "@/components/NewProposalModal";
import ManualAcceptModal from "@/components/ManualAcceptModal";
import ConvertProposalModal from "@/components/ConvertProposalModal";
import ReviewPanel from "@/components/ReviewPanel";
import { money } from "@/lib/money";


const METHOD_LABEL = { email: "email reply", verbal: "verbal / phone", signed_paper: "signed paper copy", other: "other" };

const PDLabel = {
  every_day_worked: "Every day worked",
  "5_per_week": "5 days / week (regardless of days worked)",
  "7_per_week": "7 days / week (regardless of days worked)",
};

export default function ProposalDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [repName, setRepName] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [converting, setConverting] = useState(null); // { isNewClient }

  const load = () => api.get(`/proposals/${id}`).then((r) => {
    setP(r.data);
    if (r.data?.sales_rep_id) api.get("/sales-reps").then((x) => setRepName((x.data || []).find((w) => w.id === r.data.sales_rep_id)?.name || "")).catch(() => {});
  }).catch(() => setP(null));
  useEffect(() => { load(); }, [id]);
  if (!p) return <div className="p-8">Loading…</div>;

  const isAdmin = user.role === "admin";
  const isAccepted = p.status === "accepted" || p.status === "converted";
  const canConvert = p.status === "accepted" && !p.client_id_created;
  const canAssign = !!p.project_id_created;

  const resend = async () => {
    setBusy(true);
    try {
      await api.post(`/proposals/${id}/resend`);
      toast.success("Proposal resent to the client.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail?.toString() || e.message); }
    finally { setBusy(false); }
  };

  const convert = async () => {
    setBusy(true);
    try {
      const r = await api.get("/clients");
      const exists = (Array.isArray(r.data) ? r.data : []).some((c) => c.name === p.company_name);
      setConverting({ isNewClient: !exists });
    } catch (e) { toast.error(e.response?.data?.detail?.toString() || e.message); }
    finally { setBusy(false); }
  };

  const del = async () => {
    if (!window.confirm(`Delete this proposal permanently? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/proposals/${id}`);
      toast.success("Proposal deleted.");
      nav("/proposals");
    } catch (e) { toast.error(e.response?.data?.detail?.toString() || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-4 md:p-8 max-w-4xl mx-auto">
      <Link to="/proposals" className="text-sm text-teal-700 hover:text-teal-900 font-semibold inline-flex items-center gap-1">
        <ArrowLeft size={14} weight="bold" /> Back to proposals
      </Link>
      <div className="flex items-start justify-between mt-3 mb-6 flex-wrap gap-3">
        <div>
          <div className="tinylabel">Proposal</div>
          <h1 className="text-3xl font-black">{p.company_name}</h1>
          <div className="text-slate-500 mt-1">{p.location}</div>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold border ${
            p.status === "converted" ? "bg-emerald-100 text-emerald-800 border-emerald-200"
            : p.status === "accepted" ? "bg-teal-100 text-teal-800 border-teal-200"
            : p.is_expired ? "bg-slate-200 text-slate-700 border-slate-300"
            : "bg-amber-100 text-amber-800 border-amber-200"
          }`}>{p.status === "converted" ? "Converted" : p.is_expired && p.status === "sent" ? "Expired" : p.status.replace(/_/g, " ").replace(/^./, (ch) => ch.toUpperCase())}</span>
          <a data-testid="download-proposal-pdf" href={`${API_BASE}/proposals/${p.id}/pdf`} target="_blank" rel="noreferrer"
             className="h-10 px-3 border border-slate-300 rounded-md font-semibold text-sm inline-flex items-center gap-2 hover:bg-slate-50">
            <FilePdf size={16} weight="bold" /> Preview PDF
          </a>
          {isAdmin && p.status === "sent" && (
            <button data-testid="edit-proposal-btn" onClick={() => setEditing(true)} disabled={busy}
                    className="h-10 px-3 border border-slate-300 rounded-md font-semibold text-sm inline-flex items-center gap-2 hover:bg-slate-50 disabled:opacity-50">
              <PencilSimple size={16} weight="bold" /> Edit &amp; resend
            </button>
          )}
          {isAdmin && p.status === "sent" && (
            <button data-testid="resend-proposal-btn" onClick={resend} disabled={busy}
                    className="h-10 px-3 border border-slate-300 rounded-md font-semibold text-sm inline-flex items-center gap-2 hover:bg-slate-50 disabled:opacity-50">
              <PaperPlaneTilt size={16} weight="bold" /> Resend email
            </button>
          )}
          {isAdmin && p.status === "sent" && (
            <button data-testid="accept-manual-btn" onClick={() => setAccepting(true)} disabled={busy}
                    title="Client accepted by email / phone / paper — record it so the proposal can be converted"
                    className="h-10 px-3 border border-teal-300 text-teal-800 rounded-md font-semibold text-sm inline-flex items-center gap-2 hover:bg-teal-50 disabled:opacity-50">
              <CheckCircle size={16} weight="bold" /> Mark accepted
            </button>
          )}
          {isAdmin && canConvert && (
            <button data-testid="convert-proposal-btn" onClick={convert} disabled={busy}
                    className="h-10 px-3 bg-emerald-700 hover:bg-emerald-800 text-white rounded-md font-semibold text-sm inline-flex items-center gap-2 disabled:opacity-50">
              <ArrowRight size={16} weight="bold" /> Convert to Client + Project
            </button>
          )}
          {isAdmin && canAssign && (
            <button data-testid="assign-workers-btn" onClick={() => setConverting({ alreadyConverted: true })} disabled={busy}
                    className="h-10 px-3 bg-emerald-700 hover:bg-emerald-800 text-white rounded-md font-semibold text-sm inline-flex items-center gap-2 disabled:opacity-50">
              <UsersThree size={16} weight="bold" /> Assign workers
            </button>
          )}
          {isAdmin && (
            <button data-testid="delete-proposal-btn" onClick={del} disabled={busy}
                    className="h-10 px-3 border border-slate-300 rounded-md font-semibold text-sm inline-flex items-center gap-2 hover:bg-rose-50 hover:text-rose-700 hover:border-rose-300 disabled:opacity-50">
              <Trash size={16} weight="bold" /> Delete
            </button>
          )}
        </div>
      </div>

      {isAdmin && ["pending_review", "changes_requested"].includes(p.status) && (
        <ReviewPanel kind="proposal" doc={p} repName={repName} onDone={load} />
      )}
      {!isAdmin && ["pending_review", "changes_requested"].includes(p.status) && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-4 mb-4 text-sm text-amber-900" data-testid="review-readonly-note">
          Submitted by sales rep {repName || ""} — waiting for admin approval before it goes to the client.
        </div>
      )}
      {p.status === "sent" && !p.is_expired && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-4 mb-4 text-sm text-amber-900">
          <div className="font-bold mb-1">Waiting on the client</div>
          <div>Sent to <b>{p.contact_email}</b> on {new Date(p.sent_at).toLocaleString()}. Link expires <b>{new Date(p.expires_at).toLocaleDateString()}</b>.</div>
          <div className="mt-1.5 text-xs inline-flex items-center gap-1.5" data-testid="auto-convert-status">
            <Lightning size={13} weight="fill" className={p.auto_convert !== false ? "text-emerald-600" : "text-slate-400"} />
            {p.auto_convert !== false
              ? <>On acceptance the <b>Client + Project are created automatically</b>{p.auto_convert_prefs?.project_name ? <> as “{p.auto_convert_prefs.project_name}”</> : null}.</>
              : <>Automatic conversion is <b>off</b> — you'll click Convert after they accept.</>}
          </div>
        </div>
      )}
      {p.is_expired && p.status === "sent" && (
        <div className="bg-slate-100 border border-slate-300 rounded-md p-4 mb-4 text-sm text-slate-700 flex items-start gap-2">
          <Warning size={20} weight="fill" className="text-slate-500 mt-0.5" />
          <div>The client link expired on {new Date(p.expires_at).toLocaleDateString()}. Use <b>Resend email</b> to start a fresh window.</div>
        </div>
      )}
      {isAccepted && (
        <div className="bg-teal-50 border border-teal-200 rounded-md p-4 mb-4 text-sm text-teal-900 flex items-start gap-2">
          <CheckCircle size={20} weight="fill" className="text-teal-600 mt-0.5" />
          <div>
            {p.accepted_manually ? (
              <span data-testid="accepted-manual-note">
                Marked accepted by <b>{p.accepted_recorded_by}</b> on {new Date(p.accepted_at).toLocaleString()} —
                client acceptance from <b>{p.accepted_by_name}</b>{p.accepted_by_title ? ` (${p.accepted_by_title})` : ""} via {METHOD_LABEL[p.accepted_method] || p.accepted_method}.
                {p.accepted_note && <span className="block text-xs text-teal-800 mt-0.5">Note: {p.accepted_note}</span>}
                <span className="block text-xs text-teal-700 mt-0.5">No e-signature on file — the PDF stays unsigned.</span>
              </span>
            ) : (
              <>
                Accepted by <b>{p.accepted_by_name}</b> ({p.accepted_by_title}) on {new Date(p.accepted_at).toLocaleString()}.
                {p.signature_ip && <span className="text-xs text-teal-700"> · Signed IP: {p.signature_ip}</span>}
              </>
            )}
            {p.client_id_created && (
              <div className="mt-1 text-xs" data-testid="converted-note">
                {p.auto_converted ? "Client + Project created automatically on acceptance" : "Converted to Client + Project"}
                {p.project_name_created ? <> — <b>{p.project_name_created}</b></> : null}. <Link to="/projects" className="underline font-semibold">Open projects →</Link>
                {isAdmin && <> · <button type="button" onClick={() => setConverting({ alreadyConverted: true })} className="underline font-semibold" data-testid="assign-workers-link">Assign workers</button></>}
              </div>
            )}
            {!p.client_id_created && p.auto_convert_error && (
              <div className="mt-1 text-xs text-amber-800" data-testid="auto-convert-error">
                Automatic conversion failed: {p.auto_convert_error}. Use <b>Convert to Client + Project</b> above.
              </div>
            )}
          </div>
        </div>
      )}

      <Section title="Client contact">
        <Row k="Company" v={p.company_name} />
        <Row k="Contact" v={`${p.contact_name} · ${p.contact_email} · ${p.contact_phone}`} />
        {(p.cc_emails || []).length > 0 && <Row k="Copies sent to" v={p.cc_emails.join(", ")} />}
      </Section>

      <Section title="Project">
        <Row k="Location" v={p.location} />
        <Row k="Description" v={p.description} multiline />
        <Row k="Anticipated start" v={p.anticipated_start_date} />
        <Row k="Anticipated duration" v={p.anticipated_duration} />
      </Section>

      <Section title="Commercial terms">
        <Row k="Hourly rate" v={`${money(p.hourly_rate)} / hr (straight time)`} />
        {p.minimum_hours_applies && p.minimum_hours_per_week && (
          <Row k="Minimum hours" v={`${p.minimum_hours_per_week} hrs / week guaranteed`} />
        )}
        {p.per_diem_applies && (
          <Row k="Per diem" v={`${money(p.per_diem_rate)} / day · ${PDLabel[p.per_diem_frequency] || p.per_diem_frequency}`} />
        )}
        {p.mobilization_applies && (
          <Row k="Mobilization" v={p.mobilization_hours_cap
            ? `Paid at hourly rate, cap ${p.mobilization_hours_cap} hrs each way`
            : `Paid at hourly rate`} />
        )}
        {p.special_terms && <Row k="Special terms" v={p.special_terms} multiline />}
      </Section>

      {isAccepted && !p.accepted_manually && (
        <Section title="Signature">
          <Row k="Signed by" v={`${p.accepted_by_name} — ${p.accepted_by_title}`} />
          <Row k="Typed signature" v={`/s/ ${p.signature_name}`} />
          <Row k="When" v={new Date(p.accepted_at).toLocaleString()} />
          <Row k="IP address" v={p.signature_ip || "n/a"} />
        </Section>
      )}
      {isAccepted && p.accepted_manually && (
        <Section title="Acceptance record">
          <Row k="Accepted by" v={`${p.accepted_by_name}${p.accepted_by_title ? ` — ${p.accepted_by_title}` : ""}`} />
          <Row k="Method" v={METHOD_LABEL[p.accepted_method] || p.accepted_method} />
          <Row k="Recorded by" v={p.accepted_recorded_by} />
          <Row k="When" v={new Date(p.accepted_at).toLocaleString()} />
          {p.accepted_note && <Row k="Note" v={p.accepted_note} multiline />}
        </Section>
      )}

      {accepting && <ManualAcceptModal proposal={p} onClose={() => setAccepting(false)} onAccepted={load} />}
      {converting && <ConvertProposalModal proposal={p} isNewClient={converting.isNewClient} alreadyConverted={!!converting.alreadyConverted} onClose={() => setConverting(null)} onConverted={() => { setConverting(null); load(); }} />}

      {editing && (
        <NewProposalModal initial={p} onClose={() => setEditing(false)}
                          onCreated={() => { setEditing(false); load(); }} />
      )}
    </div>
  );
}

const Section = ({ title, children }) => (
  <div className="bg-white border border-slate-200 rounded-md p-5 mb-4">
    <div className="tinylabel">{title}</div>
    <div className="mt-3 space-y-2 text-sm">{children}</div>
  </div>
);
const Row = ({ k, v, multiline }) => (
  <div className={`grid ${multiline ? "grid-cols-1 sm:grid-cols-[180px_1fr]" : "grid-cols-1 sm:grid-cols-[180px_1fr]"} gap-1 sm:gap-3`}>
    <div className="text-xs text-slate-500 uppercase tracking-widest font-semibold pt-0.5">{k}</div>
    <div className={`text-slate-800 ${multiline ? "whitespace-pre-wrap" : ""}`}>{v || "—"}</div>
  </div>
);
